import * as React from "react";
import { cn } from "../../lib/utils";
import { Lightbulb, ArrowRight, ChevronDown, ChevronUp } from "lucide-react";
import { Badge } from "../badge";
import {
  type RecommendationPriority,
  getRecommendationPriorityConfig,
} from "./ModuleScore";

// =============================================================================
// TYPES
// =============================================================================

export interface Recommendation {
  /** Unique identifier */
  id: string;
  /** Short title describing what to do */
  title: string;
  /** Detailed explanation of why this is recommended */
  description: string;
  /** Priority level - determines sort order and styling */
  priority: RecommendationPriority;
  /** Actionable next step (optional) */
  action?: string;
  /** Additional context or link (optional) */
  link?: string;
}

export interface RecommendationsListProps {
  /** List of recommendations to display */
  recommendations: Recommendation[];
  /** Maximum number to show before "show more" */
  initialLimit?: number;
  /** Custom class name */
  className?: string;
  /** Whether to group by priority */
  groupByPriority?: boolean;
}

// =============================================================================
// RECOMMENDATION CARD
// =============================================================================

interface RecommendationCardProps {
  recommendation: Recommendation;
  className?: string;
}

function RecommendationCard({ recommendation, className }: RecommendationCardProps) {
  const config = getRecommendationPriorityConfig(recommendation.priority);

  return (
    <div
      className={cn(
        "p-4 rounded-lg border border-slate-500/30 bg-slate-500/5",
        className
      )}
    >
      <div className="flex items-start gap-3">
        <Lightbulb className={cn("h-5 w-5 mt-0.5 shrink-0", config.iconColor)} />
        <div className="space-y-1 flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <h4 className="font-medium text-sm">{recommendation.title}</h4>
            <Badge
              variant="outline"
              className={cn(
                "text-[10px]",
                config.bgColor,
                config.color,
                config.borderColor
              )}
            >
              {config.label}
            </Badge>
          </div>
          <p className="text-xs text-muted-foreground">
            {recommendation.description}
          </p>
          {recommendation.action && (
            <div className="flex items-center gap-1 text-xs text-cyan-400 mt-2">
              <ArrowRight className="h-3 w-3 shrink-0" />
              <span>{recommendation.action}</span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// =============================================================================
// RECOMMENDATIONS LIST
// =============================================================================

/**
 * Unified recommendations list component.
 * Displays improvement suggestions with consistent styling across modules.
 *
 * Uses "Important/Suggested/Optional" labels instead of alarming severity colors.
 * Recommendations are suggestions, not security alerts.
 */
export function RecommendationsList({
  recommendations,
  initialLimit = 5,
  className,
  groupByPriority = false,
}: RecommendationsListProps) {
  const [showAll, setShowAll] = React.useState(false);

  if (recommendations.length === 0) {
    return null;
  }

  // Sort by priority: high -> medium -> low
  const sorted = [...recommendations].sort((a, b) => {
    const order = { high: 0, medium: 1, low: 2 };
    return order[a.priority] - order[b.priority];
  });

  const visible = showAll ? sorted : sorted.slice(0, initialLimit);
  const hasMore = sorted.length > initialLimit;

  if (groupByPriority) {
    const grouped = {
      high: sorted.filter((r) => r.priority === "high"),
      medium: sorted.filter((r) => r.priority === "medium"),
      low: sorted.filter((r) => r.priority === "low"),
    };

    return (
      <div className={cn("space-y-4", className)}>
        {grouped.high.length > 0 && (
          <div className="space-y-2">
            <h5 className="text-xs font-medium text-orange-400 uppercase tracking-wider">
              Important ({grouped.high.length})
            </h5>
            {grouped.high.map((rec) => (
              <RecommendationCard key={rec.id} recommendation={rec} />
            ))}
          </div>
        )}
        {grouped.medium.length > 0 && (
          <div className="space-y-2">
            <h5 className="text-xs font-medium text-amber-400 uppercase tracking-wider">
              Suggested ({grouped.medium.length})
            </h5>
            {grouped.medium.map((rec) => (
              <RecommendationCard key={rec.id} recommendation={rec} />
            ))}
          </div>
        )}
        {grouped.low.length > 0 && (
          <div className="space-y-2">
            <h5 className="text-xs font-medium text-slate-400 uppercase tracking-wider">
              Optional ({grouped.low.length})
            </h5>
            {grouped.low.map((rec) => (
              <RecommendationCard key={rec.id} recommendation={rec} />
            ))}
          </div>
        )}
      </div>
    );
  }

  return (
    <div className={cn("space-y-3", className)}>
      {visible.map((rec) => (
        <RecommendationCard key={rec.id} recommendation={rec} />
      ))}

      {hasMore && (
        <button
          type="button"
          onClick={() => setShowAll(!showAll)}
          className="flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground transition-colors w-full justify-center py-2"
        >
          {showAll ? (
            <>
              <ChevronUp className="h-3 w-3" />
              Show less
            </>
          ) : (
            <>
              <ChevronDown className="h-3 w-3" />
              Show {sorted.length - initialLimit} more
            </>
          )}
        </button>
      )}
    </div>
  );
}

RecommendationsList.displayName = "RecommendationsList";
